import React, { useEffect, useState } from 'react';
import { resolveGuideImageUrl, resolveGuideVideoUrl, videoMimeTypeFromUrl } from '../utils/guideImageUrl';
import { MagnifyingGlassPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

function ImageLightbox({ src, alt, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 sm:p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Enlarged screenshot"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors"
        aria-label="Close"
      >
        <XMarkIcon className="w-6 h-6" />
      </button>
      <img
        src={src}
        alt={alt || 'Guide screenshot'}
        className="max-h-[90vh] max-w-[95vw] w-auto h-auto rounded-lg shadow-2xl object-contain"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

export function GuideStepImage({ src, displayUrl, alt }) {
  const [lightbox, setLightbox] = useState(false);
  const resolvedSrc = resolveGuideImageUrl(src, displayUrl);

  if (!resolvedSrc) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setLightbox(true)}
        className="group relative mt-4 block w-full max-w-2xl text-left rounded-xl overflow-hidden border-2 border-slate-200 dark:border-slate-600 hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        <img
          src={resolvedSrc}
          alt={alt || 'Step screenshot'}
          className="w-full max-h-80 object-contain bg-slate-100 dark:bg-slate-900"
        />
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/60 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          <MagnifyingGlassPlusIcon className="w-4 h-4" />
          Click to enlarge
        </span>
      </button>
      {lightbox && <ImageLightbox src={resolvedSrc} alt={alt} onClose={() => setLightbox(false)} />}
    </>
  );
}

export function GuideStepVideo({ src, displayUrl, alt }) {
  const resolvedSrc = resolveGuideVideoUrl(src, displayUrl);
  if (!resolvedSrc) return null;

  return (
    <div className="mt-4 w-full max-w-2xl rounded-xl overflow-hidden border-2 border-slate-200 dark:border-slate-600 bg-black">
      <video
        key={resolvedSrc}
        src={resolvedSrc}
        controls
        playsInline
        preload="metadata"
        className="w-full max-h-[28rem] object-contain"
        aria-label={alt || 'Guide video'}
      >
        <source src={resolvedSrc} type={videoMimeTypeFromUrl(resolvedSrc)} />
        Your browser does not support video playback.
      </video>
    </div>
  );
}

export function GuideStepMedia({ step, alt }) {
  const label = alt || step?.title || 'Guide step';
  return (
    <>
      {step?.videoUrl && (
        <GuideStepVideo
          src={step.videoUrl}
          displayUrl={step.displayVideoUrl}
          alt={label}
        />
      )}
      {step?.imageUrl && (
        <GuideStepImage
          src={step.imageUrl}
          displayUrl={step.displayUrl}
          alt={label}
        />
      )}
    </>
  );
}
